import React from 'react'

import { calculateDiscount } from 'client/utils'
import { DiscountType } from './types'
import { DiscountPromo } from './Promo'
import { DiscountPrime } from './Prime'
import { DiscountDefault } from './Default'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'

const getType = (isPrime: boolean, promo: boolean) => {
  if (promo) {
    return DiscountType.Promo
  }
  if (isPrime) {
    return DiscountType.Prime
  }
  return DiscountType.Default
}

interface IDiscountBadge {
  price?: number
  priceBefore?: number
  discount?: number
  isPrime?: boolean
  minimumDiscountToShow?: number
}

function usePromoSource() {
  const { data: apiPromo, loading } = useUseCase('GetCurrentPromoMarketingUseCase')

  const promoActive = !!apiPromo && !apiPromo.isEmpty()
  const promoTagOverride =
    apiPromo && !apiPromo.isEmpty()
      ? { background: apiPromo.tag.background, color: apiPromo.tag.color }
      : undefined

  return { promoActive, promoTagOverride, loading }
}

function getDiscountToShow(
  explicit?: number,
  price?: number,
  priceBefore?: number,
  minimum = 0
): number | null {
  if (typeof explicit === 'number' && explicit > minimum) {
    return explicit
  }
  const computed = calculateDiscount(price, priceBefore)
  if (typeof computed === 'number' && computed > minimum) {
    return computed
  }
  return null
}

export function DiscountBadge({
  discount,
  price,
  priceBefore,
  isPrime = false,
  minimumDiscountToShow = 0,
}: Readonly<IDiscountBadge>) {
  const { promoActive, promoTagOverride, loading } = usePromoSource()
  const discountType = getType(isPrime, promoActive)

  const effectiveDiscount = getDiscountToShow(discount, price, priceBefore, minimumDiscountToShow)

  if (loading) return null
  if (!effectiveDiscount) {
    return null
  }

  if (discountType === DiscountType.Promo) {
    return (
      <DiscountPromo
        discount={effectiveDiscount}
        {...(promoTagOverride ? { tagOverride: promoTagOverride } : {})}
      />
    )
  }
  if (discountType === DiscountType.Prime) {
    return <DiscountPrime discount={effectiveDiscount} />
  }
  return <DiscountDefault discount={effectiveDiscount} />
}
